import Image from '@base/Image';
import YouTube from '@cont/FreeLesson/externalyoutube';
import styled from '@emotion/styled';
import useWindowSize from '@hooks/useWindowSize';
import MUIDialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import { segment } from '@actions/analytics';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { forwardRef, useState } from 'react';
import { useRouter } from 'next/router';
import { OFFLINE_VIDEO_TOUR_PLAYED } from '@cont/OfflineCentres/constants';
import useCentreInfo from '@api/hooks/citySpecificOfflineCentres/useCentreInfo';
import GridContainer from '@cont/CitySpecificOfflineCentres/CommonComponents';

const CardContainer = styled.div`
  position: relative;
  width: 556px;
  height: 676px;
  margin-right: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoTablet} {
    width: 100%;
    height: auto;
    margin-right: 0;
    margin-bottom: ${Spacings.SPACING_10B};
  }

  &:last-of-type {
    margin-bottom: 0;
    margin-right: 0;
  }
`;

const AppImage = styled.img`
  position: absolute;
  bottom: 0;
  right: ${Spacings.SPACING_8B};
`;

const Line1 = styled.div`
  font-size: ${Fonts.H5};
  line-height: ${Spacings.SPACING_5B};
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-primary);
  margin-top: ${Spacings.SPACING_7B};

  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_6B};
    font-size: ${Fonts.H7};
    line-height: 18px;
    font-weight: ${Fonts.LIGHT};
  }
`;

const Line2 = styled.div`
  font-size: ${Fonts.H4};
  line-height: ${Spacings.SPACING_6B};
  font-weight: ${Fonts.HEAVY};

  color: var(--color-text-primary);
  margin-top: ${Spacings.SPACING_2B};

  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_1B};
    font-size: ${Fonts.H5};
    line-height: ${Spacings.SPACING_5B};
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const Line3 = styled.div`
  font-size: ${Fonts.H6};
  line-height: ${Spacings.SPACING_6B};
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_3B};
  max-width: 50%;

  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H7};
    line-height: 18px;
  }
`;

const Card = ({ data: { mainImg, appImg, line1, line2, line3 } }) => {
  const { isMobile } = useWindowSize();
  return (
    <CardContainer>
      <Image
        src={mainImg}
        width={isMobile ? '328' : '556'}
        height={isMobile ? '328' : '544'}
        alt="main-img"
        layout={isMobile ? 'responsive' : 'intrinsic'}
      />
      <AppImage
        src={appImg}
        width={isMobile ? '120' : '154'}
        height={isMobile ? '240' : '310'}
        alt="app-img"
      />
      <Line1>{line1}</Line1>
      <Line2>{line2}</Line2>
      <Line3>{line3}</Line3>
    </CardContainer>
  );
};

const VideoCardContainer = styled.div`
  position: relative;
  overflow: hidden;
  ${mediaQuery.uptoMobile} {
    height: 328px;
    width: 100%;
  }
  ${mediaQuery.uptoTablet} {
    height: 328px;
  }
`;

const ContentBlock = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_7B};
  background: var(--color-base-1);

  ${mediaQuery.uptoTablet} {
    padding: ${Spacings.SPACING_4B};
  }
`;

const VideoCardLine1 = styled.div`
  font-size: ${Fonts.H5};
  line-height: ${Spacings.SPACING_5B};
  font-weight: ${Fonts.SEMIBOLD};

  color: var(--color-text-primary);

  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H7};
    line-height: 18px;
    font-weight: ${Fonts.LIGHT};
  }
`;

const VideoCardLine2 = styled.div`
  font-size: ${Fonts.H4};
  line-height: ${Spacings.SPACING_6B};
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-primary);
  margin-top: 6px;

  ${mediaQuery.uptoTablet} {
    margin-top: 3px;
    font-size: ${Fonts.H6};
    line-height: 18px;
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const PlayBtn = styled.img`
  position: absolute;
  bottom: ${Spacings.SPACING_7B};
  left: ${Spacings.SPACING_7B};
  cursor: pointer;

  ${mediaQuery.uptoTablet} {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const StyledImg = styled(Image)`
  ${mediaQuery.uptoTablet} {
    left: calc(-50%) !important;
  }
`;

const opts = {
  height: '100%',
  width: '100%',
  playerVars: {
    autoplay: 1,
    showinfo: 0,
    rel: 0
  }
};

const Transition = forwardRef(function Transition(props, ref) {
  // @ts-ignore
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(MUIDialog)`
  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    position: absolute;
    background: var(--color-text-primary);
    height: 80%;
    width: 88%;
    max-width: 88%;
    ${mediaQuery.uptoMobile} {
      margin: 16px auto;
      height: 30%;
    }
  }
`;

const VideoCard = ({ data: { videoUrl, thumbnailUrl, line1, line2 }, src }) => {
  const { isMobile } = useWindowSize();
  const [isOpen, setIsOpen] = useState(false);
  const {
    query: { goalUID: routeGoalUID, centreUID }
  } = useRouter();
  const { centreInfo } = useCentreInfo(routeGoalUID, centreUID);
  const { centreName, cityName } = centreInfo || {};

  const handleClick = () => {
    setIsOpen(true);
    segment.track(OFFLINE_VIDEO_TOUR_PLAYED, {
      tour_source: src,
      centre_name: centreName,
      city: cityName,
      goal_uid: routeGoalUID
    });
  };
  return (
    <VideoCardContainer>
      <StyledImg
        src={thumbnailUrl}
        width={isMobile ? '757' : '1136'}
        height={isMobile ? '400' : '600'}
        alt="thumbnail"
        unoptimized
        layout={isMobile ? 'fixed' : 'intrinsic'}
      />
      <ContentBlock>
        <VideoCardLine1>{line1}</VideoCardLine1>
        <VideoCardLine2>{line2}</VideoCardLine2>
      </ContentBlock>
      <PlayBtn
        src={
          isMobile
            ? '/static/images/offline-centres/mobile-play.svg'
            : '/static/images/offline-centres/desktop-play.svg'
        }
        width={isMobile ? '39' : '157'}
        height={isMobile ? '39' : '60'}
        alt="play"
        onClick={handleClick}
      />
      <StyledDialog
        open={isOpen}
        // @ts-ignore
        TransitionComponent={Transition}
        onClose={() => setIsOpen(false)}
        $center
        $height="80%"
        $width="88%"
      >
        <YouTube
          opts={opts}
          videoId={videoUrl}
          onEnd={() => setIsOpen(false)}
          useWrapper={false}
        />
      </StyledDialog>
    </VideoCardContainer>
  );
};

const Container = styled.div`
  grid-column: span 24;
  width: 100%;
  margin-bottom: ${Spacings.SPACING_30B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_10B};
  }
`;

const Heading = styled.div`
  font-size: ${Fonts.H2};
  line-height: 38px;
  font-weight: 700;
  max-width: 450px;
  color: var(--color-text-primary);
  margin-bottom: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H4};
    line-height: 24px;
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const Offerings = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoTablet} {
    flex-direction: column;
  }
`;

const Offering = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${Spacings.SPACING_12B};

  ${mediaQuery.uptoTablet} {
    margin-right: 0;
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const Text = styled.div`
  font-size: ${Fonts.H5};
  line-height: 20px;
  color: var(--color-text-primary);
  margin-left: ${Spacings.SPACING_5B};
  max-width: 120px;
`;

const Banners = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_10B};
    flex-direction: column;
  }
`;

const LiveClassroon = ({
  data: { heading, offerings, content, videoCard },
  liveClassroomRef,
  src
}) => {
  return (
    <GridContainer>
      <Container ref={liveClassroomRef}>
        <Heading>{heading}</Heading>
        <Offerings>
          {offerings.map(({ icon, description }) => {
            return (
              <Offering key={description}>
                <Image src={icon} width="52" height="52" alt="icon" />
                <Text>{description}</Text>
              </Offering>
            );
          })}
        </Offerings>
        <VideoCard data={videoCard} src={src} />
        <Banners>
          {content.map((data) => {
            return <Card key={data.line1} data={data} />;
          })}
        </Banners>
      </Container>
    </GridContainer>
  );
};

export default LiveClassroon;
